import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import { useContext } from 'react'
import { getThemeClassName } from 'lib/theme/themeUtils'
import Pane from 'components/Common/Pane'
import LayoutContainer from 'components/Common/LayoutContainer'
import SolidPlayIcon from 'LuxKit/Icons/solid/SolidPlayIcon'
import HeightFillingImage from 'components/Common/HeightFillingImage'
import VideoLightbox from 'LuxKit/Carousel/VideoLightbox'
import ModalContext from 'contexts/ModalContext'
import { renderMarkdown } from 'lib/string/markdownUtils'
import clsx from 'clsx'
import Caption from 'LuxKit/Typography/Caption'
import TextButton from 'LuxKit/Button/TextButton'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import Sheet from 'components/utils/Sheet'
import useRebrandVersion from 'hooks/useRebrandVersion'
import { REBRAND_PALETTE_CLASS } from 'lib/theme/paletteVersion'
import styled, { css } from 'styled-components'
import { getContentfulVideoBlockSurface } from './contentfulRebrandSurfaces'
import { rebrandTealDarkBackground } from './contentfulRebrandTealSurface'

/**
 * Path-scoped beige only under rebrand. Legacy dark blocks keep full inverse tokens
 * (no partial CSS var remap). Prefer &&.class over nested && under body…
 */
const pathScopedBeigeDarkBackground = css`
  body.${REBRAND_PALETTE_CLASS} &&.path-scoped-beige-dark {
    background-color: var(--palette-neutral-surface-light-background);
  }
`

const Root = styled(Sheet)`
  &.background-color-white {
    background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  }

  &.background-color-grey {
    background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  }

  /*
   * Path-scoped campaigns pass rebrandBeigeScope. Beige class is only applied under
   * rebrand; legacy dark blocks stay paletteType=inverse with full theme tokens.
   */
  ${pathScopedBeigeDarkBackground}
  ${rebrandTealDarkBackground}
`

const Grid = styled(Pane)`
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};

  &.background-color-white {
    background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  }

  &.background-color-grey {
    background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  }

  ${pathScopedBeigeDarkBackground}
  ${rebrandTealDarkBackground}

  &.show-image {
    grid-template-areas:
    'content'
    'video';

    &.content-position-end {
      grid-template-areas:
        'video'
        'content';

      ${mediaQueryUp.tablet} {
        grid-template-areas: 'video content';
      }
    }

    ${mediaQueryUp.tablet} {
      grid-template-columns: 1fr 1fr;
      grid-template-areas: 'content video';
    }
  }

  width: 100%;
  display: grid;
  gap: ${rem(20)};
  padding: ${rem(32)} 0;

  ${mediaQueryUp.tablet} {
    gap: ${rem(20)} ${rem(40)};
    padding: ${rem(60)} 0;
    align-items: center;
  }
`

const Content = styled(Group)`
  grid-area: content;
`

const Video = styled.div`
  grid-area: video;
`

const PlayButton = styled.div`
  --contentful-video-play-button-inline-translate: -50%;

  position: absolute;
  inset-inline-start: 50%;
  top: 50%;
  transition: transform 0.2s;
  transform: translateY(-50%) translateX(var(--contentful-video-play-button-inline-translate)) scale(1);
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${rem(48)};
  width: ${rem(48)};

  [dir='rtl'] & {
    --contentful-video-play-button-inline-translate: 50%;
  }

  ${mediaQueryDown.mobile} {
    height: ${rem(36)};
    width: ${rem(36)};
  }
`

const StyledPlayIcon = styled(SolidPlayIcon)`
  height: ${rem(24)};
  width: ${rem(24)};

  ${mediaQueryDown.mobile} {
    height: ${rem(20)};
    width: ${rem(20)};
  }
`

const VimeoWrapper = styled.div`
  width: 100%;
  position: relative;
  background-color: ${props => props.theme.palette.neutral.surface.darkBackground};

  > * {
    pointer-events: none;
  }

  &:focus, &:hover {
    > ${PlayButton} {
      transform: translateY(-50%) translateX(var(--contentful-video-play-button-inline-translate)) scale(1.2);
    }
  }
`

export function ContentfulVideoBlock({
  title,
  subtitle,
  bodyText,
  videoId,
  image,
  backgroundColor,
  caption,
  contentPosition,
  buttonBlocks,
  contentAlign,
  buttonDirection,
  rebrandBeigeScope = false,
}: App.ComponentVideoBlock & { rebrandBeigeScope?: boolean }) {
  const markedBodyText = renderMarkdown(bodyText)
  const showLightbox = useContext(ModalContext)
  const { paletteType, isPathScopedBeigeDark } = getContentfulVideoBlockSurface({
    backgroundColor,
    rebrandBeigeScope,
    isRebrand: useRebrandVersion() === 'rebrand',
  })
  return (
    <Root
      paletteType={paletteType}
      background="normal-background"
      className={clsx(`background-color-${backgroundColor}`, {
        'path-scoped-beige-dark': isPathScopedBeigeDark,
      })}
    >
      <LayoutContainer>
        <Grid
          className={clsx(
            `background-color-${backgroundColor}`,
            `content-position-${contentPosition}`,
            {
              'show-image': image.id,
              'path-scoped-beige-dark': isPathScopedBeigeDark,
            },
          )}
        >
          <Content direction="vertical" gap={16} desktopGap={20}>
            <Group direction="vertical" gap={4}>
              {!!caption && <Caption variant="small" colour="light-foreground-alt" weight="bold" align={contentAlign}>{caption}</Caption>}
              <Heading variant="heading2" colour="normal-contrast" dangerouslySetInnerHTML={{ __html: title }} align={contentAlign} />
              {!!subtitle && <BodyText variant="large" colour="light-foreground-alt" align={contentAlign}>{subtitle}</BodyText>}
            </Group>
            <BodyText variant="large" colour="light-foreground-alt" dangerouslySetInnerHTML={{ __html: markedBodyText }} align={contentAlign} />
            {!!buttonBlocks?.length && <Group direction={buttonDirection} gap={8} horizontalAlign={contentAlign}>
              {buttonBlocks.map((buttonBlock, index) =>
                <ProductPaletteProvider palette={buttonBlock.productTheme} key={`${index}-${buttonBlock.title}`}>
                  <TextButton
                    fit={buttonBlock.fit}
                    kind={buttonBlock.kind}
                    {...(buttonBlock.linkTo.startsWith('http') ? { href: buttonBlock.linkTo } : { to: buttonBlock.linkTo })}
                    size={buttonBlock.size}
                  >
                    {buttonBlock.title}
                  </TextButton>
                </ProductPaletteProvider>)}
            </Group>}
          </Content>
          {!!image.id && <Video>
            {!!videoId && <VimeoWrapper className={getThemeClassName({ mobile: 'default' })} onClick={() => showLightbox(<VideoLightbox videoId={videoId} playOnMount />)}>
              <HeightFillingImage
                fit="center"
                id={image.id}
                alt={title}
                mobileAspectRatio="16:9"
              />
              <PlayButton>
                <StyledPlayIcon />
              </PlayButton>
            </VimeoWrapper>}
            {!videoId && <HeightFillingImage
              fit="center"
              id={image.id}
              alt={title}
              mobileAspectRatio="16:9"
            />}
          </Video>}
        </Grid>
      </LayoutContainer>
    </Root>
  )
}
